/**
 * Shared vocabulary for the Instrument Recommendations screens.
 *
 * A recommendation pick is a PublicEquity instrument on one exchange — never a
 * Company and never a ticker. Everything here exists to keep that identity rule
 * and the enum spellings the backend expects in one place.
 */

/** Recommendations are PublicEquity-only. Legacy rows may read `STOCK`; we never write it. */
export const INSTRUMENT_KEY = 'PUBLIC_EQUITY';

export const LANGUAGE_OPTIONS = [
  {value: 'EN', label: 'English'},
  {value: 'FR', label: 'French'},
];

export const languageLabel = language =>
  language === 'FR' ? 'French' : 'English';

export const otherLanguage = language => (language === 'EN' ? 'FR' : 'EN');

export const PICK_TYPES = ['BUY', 'HOLD', 'SELL'];

export const typeLabel = type =>
  ({BUY: 'Buy', HOLD: 'Hold', SELL: 'Sell'}[type] || type);

export const SOURCE_OPTIONS = [
  {value: 'JDA', label: 'JDA'},
  {value: 'PHOENIX_CAPITAL', label: 'Phoenix Capital'},
  {value: 'TIER_1_RESEARCH_FIRM', label: 'Tier 1 Research Firm'},
  {value: 'DABA_INTELLIGENCE', label: 'Daba Intelligence'},
];

export const sourceLabel = source =>
  SOURCE_OPTIONS.find(option => option.value === source)?.label ||
  String(source || '').replace(/_/g, ' ');

/**
 * The development database has active PDF templates for BUY and SELL only. The
 * mutation accepts HOLD and reports "initiated", but no PDF is ever produced —
 * so HOLD pick management stays open while its report action stays disabled.
 */
export const TYPES_WITHOUT_REPORT_TEMPLATE = ['HOLD'];

export const canGenerateReport = type =>
  !TYPES_WITHOUT_REPORT_TEMPLATE.includes(type);

export const NO_TEMPLATE_MESSAGE = 'HOLD report template not configured';

/** The report builder reads at most this many picks for one source/type. */
export const MAX_PICKS_PER_REPORT = 50;

/** Report generation is async with no job id — poll only while a URL is missing. */
export const REPORT_POLL_INTERVAL_MS = 4000;
export const REPORT_POLL_TIMEOUT_MS = 90000;

export const OBJECT_ID_PATTERN = /^[a-f\d]{24}$/i;

/**
 * Recommendation operations return unions, so HTTP 200 is not success. Every
 * caller branches on this before touching the payload.
 */
export const isGqlError = result =>
  !result ||
  result.__typename === 'Error' ||
  (typeof result.statusCode === 'number' && result.statusCode >= 400);

/** Server messages are useful — surface them rather than a generic failure. */
export const errorMessage = (result, fallback = 'Something went wrong') =>
  result?.message || fallback;

/**
 * Reads a mutation result into either its payload or one message to show.
 *
 * Two failure channels have to be handled: the `Error` union member, and
 * top-level `errors[]` — where GraphQL validation, malformed enums and non-null
 * violations land. With `errorPolicy: 'all'` the second kind resolves quietly
 * instead of throwing, so it would otherwise pass for success.
 */
export const readMutation = ({data, errors}, field) => {
  if (errors?.length) return {error: errors[0].message};
  const payload = data?.[field];
  if (isGqlError(payload)) return {error: errorMessage(payload)};
  return {payload};
};

/**
 * Analysis HTML is rendered unescaped into the PDF template, so strip anything
 * executable before it leaves the browser. Quill's own formats cover the normal
 * path; this catches pasted markup.
 */
export const sanitizeAnalysis = (html = '') =>
  String(html)
    .replace(
      /<\s*(script|style|iframe|object|embed|link|meta)[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi,
      '',
    )
    .replace(/<\s*(script|style|iframe|object|embed|link|meta)[^>]*\/?>/gi, '')
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/\s(href|src)\s*=\s*("|')\s*javascript:[^"']*\2/gi, ' $1=$2#$2')
    .trim();

/** Quill leaves `<p><br></p>` behind when the editor is cleared. */
export const isAnalysisEmpty = (html = '') =>
  !String(html)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();

/**
 * The API does not return potential gain/drop. Positive on a BUY reads as
 * potential gain; on HOLD/SELL we stay neutral and call it a projected move.
 */
export const projectedMove = (priceAsOf, projectedPriceBefore) => {
  const base = Number(priceAsOf);
  const target = Number(projectedPriceBefore);
  if (!base || base <= 0 || !Number.isFinite(target)) return null;
  return ((target - base) / base) * 100;
};

export const moveLabel = type =>
  type === 'BUY' ? 'Potential gain' : 'Projected move';

export const formatPrice = (value, currencyCode) => {
  if (value === null || value === undefined || value === '') return '-';
  const amount = Number(value).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });
  return currencyCode ? `${amount} ${currencyCode}` : amount;
};
